import { Component, Inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UpgradeItemType, UpgradeLabourType, UpgradeLineItem } from '@app/models/closedown-detail.model';

export interface UpgradeLineItemDialogData {
  itemTypes: UpgradeItemType[];
  labourTypes: UpgradeLabourType[];
  taxRate: number;
  currency: string;
  context?: 'upgrade' | 'invoice';
  lineItem?: UpgradeLineItem;
}

@Component({
  selector: 'app-upgrade-line-item-dialog',
  templateUrl: './upgrade-line-item-dialog.component.html',
  styleUrls: ['./upgrade-line-item-dialog.component.scss'],
  standalone: false
})
export class UpgradeLineItemDialogComponent {
  readonly form = this.formBuilder.group({
    itemTypeId: [null as string | null, Validators.required],
    labourType: [''],
    description: [''],
    unitPrice: [0, [Validators.required, Validators.min(0.01)]],
    quantity: [0, [Validators.required, Validators.min(0.01)]],
    timeQuantity: ['00:00', Validators.pattern(/^\d{1,3}:[0-5]\d$/)]
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: UpgradeLineItemDialogData,
    private readonly dialogRef: MatDialogRef<UpgradeLineItemDialogComponent>,
    private readonly formBuilder: FormBuilder
  ) {
    if (data.lineItem) {
      const itemType = data.itemTypes.find(option => option.name === data.lineItem?.itemType);
      const labourType = data.labourTypes.find(option => option.name === data.lineItem?.itemDescription);
      this.form.patchValue({
        itemTypeId: itemType?.id ?? null,
        labourType: labourType?.id ?? '',
        description: labourType ? '' : data.lineItem.itemDescription,
        unitPrice: data.lineItem.unitPrice,
        quantity: data.lineItem.itemQuantity,
        timeQuantity: this.toTime(data.lineItem.itemQuantity)
      });
    }
  }

  get selectedType(): UpgradeItemType | undefined {
    return this.data.itemTypes.find(option => option.id === this.form.controls.itemTypeId.value);
  }

  get isLabour(): boolean {
    return this.selectedType?.name?.toUpperCase() === 'LABOUR';
  }

  get usesTime(): boolean {
    return this.selectedType?.unitOfMeasureCode?.toUpperCase() === 'T';
  }

  get usesQuantity(): boolean {
    const unitOfMeasureCode = this.selectedType?.unitOfMeasureCode?.toUpperCase();
    return unitOfMeasureCode === 'T' || unitOfMeasureCode === 'Q';
  }

  get quantity(): number {
    if (!this.usesQuantity) {
      return 1;
    }

    if (this.usesTime) {
      const [hours, minutes] = this.form.controls.timeQuantity.value?.split(':').map(Number) || [];
      return (hours || 0) + ((minutes || 0) / 60);
    }

    return this.form.controls.quantity.value || 0;
  }

  get net(): number {
    return this.round((this.form.controls.unitPrice.value || 0) * this.quantity);
  }

  get tax(): number {
    return this.round(this.net * this.data.taxRate);
  }

  get gross(): number {
    return this.round(this.net + this.tax);
  }

  get canSave(): boolean {
    const description = this.isLabour
      ? this.form.controls.labourType.value
      : this.form.controls.description.value;
    const quantityValid = !this.usesQuantity || this.quantity > 0;
    return this.form.controls.itemTypeId.valid
      && this.form.controls.unitPrice.valid
      && Boolean(description?.trim())
      && quantityValid
      && (!this.usesTime || this.form.controls.timeQuantity.valid);
  }

  itemTypeChanged(): void {
    const fixedQuantity = !this.usesQuantity;
    this.form.patchValue({
      labourType: '',
      description: '',
      quantity: fixedQuantity ? 1 : 0,
      timeQuantity: '00:00',
      unitPrice: 0
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (!this.canSave || !this.selectedType) {
      this.form.markAllAsTouched();
      return;
    }

    const labourType = this.data.labourTypes.find(option => option.id === this.form.controls.labourType.value);
    this.dialogRef.close({
      itemID: this.selectedType.id!,
      itemType: this.selectedType.name ?? '',
      itemCode: this.selectedType.itemCode ?? '',
      ...(this.selectedType.unitOfMeasureCode
        ? { itemUnitOfMeasure: this.selectedType.unitOfMeasureCode }
        : {}),
      itemDescription: this.isLabour
        ? labourType?.name?.trim() ?? ''
        : this.form.controls.description.value!.trim(),
      itemQuantity: this.round(this.quantity),
      unitPrice: this.round(this.form.controls.unitPrice.value!),
      lineTax: this.tax,
      lineGross: this.gross
    } satisfies UpgradeLineItem);
  }

  private toTime(quantity: number): string {
    const hours = Math.floor(quantity);
    const minutes = Math.round((quantity - hours) * 60 / 5) * 5;
    return `${String(hours).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
  }

  private round(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }
}